import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, TableCellProps, TextField } from "@material-ui/core";
import { Link } from "react-router-dom";
import { Typography } from "@welldigital/components";
import CustomTable, {
  CustomTableHeaderProps,
} from "pages/ClinicalReviewPage/components/Table";
import Pagination from "pages/ClinicalReviewPage/components/Pagination";
import {
  fetchSubscriptionsList,
  updateSubscriptionNextOrderDate,
} from "pages/ClinicalReviewPage/ClinicalReviewApi";
import {
  ClinicalSubscription,
  ClinicalSubscriptionsResponse,
  SubscriptionDetailsType,
} from "pages/ClinicalReviewPage/Types";
import { useCurrentUserEmail } from "services/auth-hooks";
import { formatDate } from "utils/time";
import "./index.scss";

const PAGE_SIZE = 10;
const DAY_IN_MS = 24 * 60 * 60 * 1000;

const CELL_PROPS: TableCellProps = { align: "left" };

type SubscriptionTableRow = ClinicalSubscription & {
  daysUntilNextOrder?: number;
};

const calculateDaysUntil = (nextOrderDate: string | undefined) => {
  if (!nextOrderDate) {
    return undefined;
  }

  const targetDate = new Date(nextOrderDate);
  if (Number.isNaN(targetDate.getTime())) {
    return undefined;
  }

  const now = new Date();
  const startOfTodayUTC = Date.UTC(
    now.getUTCFullYear(),
    now.getUTCMonth(),
    now.getUTCDate(),
  );
  const startOfTargetUTC = Date.UTC(
    targetDate.getUTCFullYear(),
    targetDate.getUTCMonth(),
    targetDate.getUTCDate(),
  );

  return Math.ceil((startOfTargetUTC - startOfTodayUTC) / DAY_IN_MS);
};

const toDateInputValue = (dateValue: string | undefined) => {
  if (!dateValue) {
    return "";
  }

  const parsedDate = new Date(dateValue);
  if (Number.isNaN(parsedDate.getTime())) {
    return "";
  }

  const year = parsedDate.getUTCFullYear();
  const month = `${parsedDate.getUTCMonth() + 1}`.padStart(2, "0");
  const day = `${parsedDate.getUTCDate()}`.padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const toIsoFromDateInput = (dateValue: string) => {
  const [year, month, day] = dateValue.split("-").map(Number);
  if (!year || !month || !day) {
    return "";
  }

  return new Date(Date.UTC(year, month - 1, day, 12, 0, 0)).toISOString();
};

const formatSubscriptionStatus = (status?: string) => {
  const normalizedStatus = status?.trim().toLowerCase();

  if (!normalizedStatus) {
    return "-";
  }

  if (normalizedStatus === "cancel") {
    return "Cancelled";
  }

  return normalizedStatus
    .split(/[_\-\s]+/)
    .filter(Boolean)
    .map((part) => `${part.charAt(0).toUpperCase()}${part.slice(1)}`)
    .join(" ");
};

const getRequestErrorMessage = (error: unknown) => {
  const requestError = error as {
    response?: { data?: { message?: string; error?: string } | string };
    message?: string;
  };

  const responseData = requestError?.response?.data;
  if (typeof responseData === "string" && responseData.trim()) {
    return responseData;
  }
  if (
    responseData &&
    typeof responseData === "object" &&
    "message" in responseData &&
    typeof responseData.message === "string" &&
    responseData.message.trim()
  ) {
    return responseData.message;
  }
  if (
    responseData &&
    typeof responseData === "object" &&
    "error" in responseData &&
    typeof responseData.error === "string" &&
    responseData.error.trim()
  ) {
    return responseData.error;
  }

  return requestError?.message || "Unable to update next order date";
};

const mapSubscriptionRows = (
  rows: ClinicalSubscription[],
): SubscriptionTableRow[] =>
  rows.map((row) => ({
    ...row,
    daysUntilNextOrder: calculateDaysUntil(row.nextOrderDate),
  }));

const ClinicalReviewSubscriptionsPage: React.FC = () => {
  const email = useCurrentUserEmail();
  const [subscriptions, setSubscriptions] = useState<SubscriptionTableRow[]>(
    [],
  );
  const [currentPage, setCurrentPage] = useState(1);
  const [currentCursor, setCurrentCursor] = useState<string | undefined>(
    undefined,
  );
  const [nextCursor, setNextCursor] = useState<string | undefined>(undefined);
  const [cursorHistory, setCursorHistory] = useState<(string | undefined)[]>(
    [],
  );
  const [hasNext, setHasNext] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [updateError, setUpdateError] = useState("");
  const [updateSuccess, setUpdateSuccess] = useState("");
  const [editingSubscriptionId, setEditingSubscriptionId] = useState<
    string | null
  >(null);
  const [editingNextOrderDate, setEditingNextOrderDate] = useState("");
  const [savingSubscriptionId, setSavingSubscriptionId] = useState<
    string | null
  >(null);

  const loadSubscriptions = useCallback(async () => {
    setLoading(true);
    setLoadError("");
    setEditingSubscriptionId(null);
    setEditingNextOrderDate("");

    try {
      const response = (await fetchSubscriptionsList({
        limit: PAGE_SIZE,
        cursor: currentCursor,
      })) as ClinicalSubscriptionsResponse;

      setSubscriptions(mapSubscriptionRows(response?.subscriptions ?? []));
      setHasNext(Boolean(response?.hasNext));
      setNextCursor(response?.nextCursor || undefined);
    } catch (error) {
      setSubscriptions([]);
      setHasNext(false);
      setNextCursor(undefined);
      setLoadError("Unable to load subscriptions");
    } finally {
      setLoading(false);
    }
  }, [currentCursor]);

  useEffect(() => {
    loadSubscriptions();
  }, [loadSubscriptions]);

  const startEditingNextOrderDate = useCallback((row: SubscriptionTableRow) => {
    setEditingSubscriptionId(row.subscriptionId);
    setEditingNextOrderDate(toDateInputValue(row.nextOrderDate));
    setUpdateError("");
    setUpdateSuccess("");
  }, []);

  const stopEditingNextOrderDate = useCallback(() => {
    setEditingSubscriptionId(null);
    setEditingNextOrderDate("");
    setUpdateError("");
  }, []);

  const saveNextOrderDate = useCallback(
    async (subscriptionId: string) => {
      if (!editingNextOrderDate) {
        setUpdateError("Please select a date");
        return;
      }

      setSavingSubscriptionId(subscriptionId);
      setUpdateError("");
      setUpdateSuccess("");

      try {
        const updatedSubscription =
          (await updateSubscriptionNextOrderDate(subscriptionId, {
            nextOrderDate: editingNextOrderDate,
            updatedBy: email || "clinical-review",
          })) as SubscriptionDetailsType;

        setSubscriptions((prevState) =>
          prevState.map((row) => {
            if (row.subscriptionId !== subscriptionId) {
              return row;
            }

            const nextOrderDate =
              updatedSubscription?.nextOrderDate ||
              toIsoFromDateInput(editingNextOrderDate) ||
              row.nextOrderDate;

            return {
              ...row,
              nextOrderDate,
              daysUntilNextOrder: calculateDaysUntil(nextOrderDate),
              subscriptionStatus:
                updatedSubscription?.status ?? row.subscriptionStatus,
            };
          }),
        );

        setUpdateSuccess(
          `Next order date updated for subscription ${subscriptionId}`,
        );
        setEditingSubscriptionId(null);
        setEditingNextOrderDate("");
      } catch (error) {
        setUpdateError(getRequestErrorMessage(error));
      } finally {
        setSavingSubscriptionId(null);
      }
    },
    [editingNextOrderDate, email],
  );

  const handlePageChange = useCallback(
    (requestedPage: number) => {
      if (requestedPage === currentPage) {
        return;
      }

      if (requestedPage > currentPage) {
        if (!hasNext || !nextCursor) {
          return;
        }

        setCursorHistory((prevState) => [...prevState, currentCursor]);
        setCurrentCursor(nextCursor);
        setCurrentPage(requestedPage);
        return;
      }

      if (cursorHistory.length === 0) {
        return;
      }

      const previousCursor = cursorHistory[cursorHistory.length - 1];
      setCursorHistory((prevState) => prevState.slice(0, -1));
      setCurrentCursor(previousCursor);
      setCurrentPage(requestedPage);
    },
    [currentCursor, currentPage, cursorHistory, hasNext, nextCursor],
  );

  const columns: CustomTableHeaderProps<SubscriptionTableRow>[] = useMemo(
    () => [
      {
        header: "Latest Order",
        keyExtractor: "latestOrderId",
        cellProps: { ...CELL_PROPS, width: "16%" },
        transform: (data) => (
          <Link to={`/clinical-review/order/${data.latestOrderId}`}>
            {data.latestOrderId}
          </Link>
        ),
      },
      {
        header: "Customer Email",
        keyExtractor: "customerEmail",
        cellProps: { ...CELL_PROPS, width: "14%" },
        transform: (data) => data.customerEmail || "-",
      },
      {
        header: "Issue",
        keyExtractor: "subscriptionIssueNumber",
        cellProps: { ...CELL_PROPS, width: "12%" },
        transform: (data) => {
          if (!data.subscriptionIssueNumber) {
            return "-";
          }
          return data.maxIssueLimit
            ? `${data.subscriptionIssueNumber} of ${data.maxIssueLimit}`
            : data.subscriptionIssueNumber;
        },
      },
      {
        header: "Order Status",
        keyExtractor: "latestOrderStatus",
        cellProps: { ...CELL_PROPS, width: "14%" },
      },
      {
        header: "Next Order Date",
        keyExtractor: "nextOrderDate",
        cellProps: { ...CELL_PROPS, width: "17%" },
        transform: (data) => {
          const isEditing = editingSubscriptionId === data.subscriptionId;
          const isSaving = savingSubscriptionId === data.subscriptionId;

          if (isEditing) {
            return (
              <div className="crp-next-order-editor">
                <TextField
                  type="date"
                  value={editingNextOrderDate}
                  onChange={(event) =>
                    setEditingNextOrderDate(event.target.value)
                  }
                  variant="outlined"
                  size="small"
                  fullWidth
                />
                <div className="crp-next-order-editor-actions">
                  <Button
                    type="button"
                    color="primary"
                    variant="contained"
                    size="small"
                    onClick={() => saveNextOrderDate(data.subscriptionId)}
                    disabled={isSaving || !editingNextOrderDate}
                  >
                    {isSaving ? "Saving..." : "Save"}
                  </Button>
                  <Button
                    type="button"
                    size="small"
                    onClick={stopEditingNextOrderDate}
                    disabled={isSaving}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            );
          }

          return (
            <div className="crp-next-order-display">
              <span>
                {data.nextOrderDate
                  ? formatDate(data.nextOrderDate, "dd/MM/yy")
                  : "-"}
              </span>
              <Button
                type="button"
                color="primary"
                size="small"
                onClick={() => startEditingNextOrderDate(data)}
              >
                Edit
              </Button>
            </div>
          );
        },
      },
      {
        header: "Days To Next",
        keyExtractor: "daysUntilNextOrder",
        cellProps: { ...CELL_PROPS, width: "10%" },
        transform: (data) => {
          if (data.daysUntilNextOrder === undefined) {
            return "-";
          }
          if (data.daysUntilNextOrder < 0) {
            return `Overdue ${Math.abs(data.daysUntilNextOrder)}d`;
          }
          if (data.daysUntilNextOrder === 0) {
            return "Today";
          }
          return `${data.daysUntilNextOrder}d`;
        },
      },
      {
        header: "Status",
        keyExtractor: "subscriptionStatus",
        cellProps: { ...CELL_PROPS, width: "12%" },
        transform: (data) => formatSubscriptionStatus(data.subscriptionStatus),
      },
    ],
    [
      editingNextOrderDate,
      editingSubscriptionId,
      saveNextOrderDate,
      savingSubscriptionId,
      startEditingNextOrderDate,
      stopEditingNextOrderDate,
    ],
  );

  return (
    <div className="crp-subscriptions-container">
      <Typography className="crp-subscriptions-title">Subscriptions</Typography>

      {loadError ? (
        <Typography className="crp-subscriptions-message">
          {loadError}
        </Typography>
      ) : null}
      {updateError ? (
        <Typography className="crp-subscriptions-message">
          {updateError}
        </Typography>
      ) : null}
      {updateSuccess ? (
        <Typography className="crp-subscriptions-message-success">
          {updateSuccess}
        </Typography>
      ) : null}

      <CustomTable
        sortKey=""
        isAscending={false}
        data={subscriptions}
        loading={loading}
        columns={columns}
      />

      {!loading && subscriptions.length === 0 ? (
        <Typography>No subscriptions found</Typography>
      ) : null}

      <Pagination
        mode="cursor"
        hasNext={hasNext}
        totalItems={0}
        pageSize={PAGE_SIZE}
        currentPage={currentPage}
        onPageChange={handlePageChange}
      />
    </div>
  );
};

export default ClinicalReviewSubscriptionsPage;
